'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { normalizeUserProfile, type UserProfile } from '@shared/models/user';

interface CurrentUserContextValue {
  user: UserProfile | null;
  setUser: (user: UserProfile) => void;
  clearUser: () => void;
}

const UserContext = createContext<CurrentUserContextValue | undefined>(undefined);
const USER_STORAGE_KEY = 'miconnect-user';

function readStoredUser(): UserProfile | null {
  try {
    const storedUser = sessionStorage.getItem(USER_STORAGE_KEY);

    if (!storedUser) {
      return null;
    }

    const user = normalizeUserProfile(JSON.parse(storedUser));

    if (!user) {
      sessionStorage.removeItem(USER_STORAGE_KEY);
    }

    return user;
  } catch {
    writeStoredUser(null);
    return null;
  }
}

function writeStoredUser(user: UserProfile | null): void {
  try {
    if (user) {
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      sessionStorage.removeItem(USER_STORAGE_KEY);
    }
  } catch {
    // The in-memory context remains usable when browser storage is unavailable.
  }
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<UserProfile | null>(null);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setUserState(readStoredUser());
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, []);

  const setUser = useCallback((nextUser: UserProfile) => {
    const normalizedUser = normalizeUserProfile(nextUser);

    setUserState(normalizedUser);
    writeStoredUser(normalizedUser);
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
    writeStoredUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      setUser,
      clearUser,
    }),
    [user, setUser, clearUser]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useCurrentUser(): CurrentUserContextValue {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useCurrentUser must be used within UserProvider');
  }

  return context;
}
